import { BuyAndGetConditionsTypes, DiscountCodeTypes } from "../../enum";
import { calculateProductTaxBreakdown, getProductTaxesPercentage } from "./helpers";
import { resolveBillableQuantity } from "./billableQuantity";
import { isNoChargeReturnLine } from "@washday/utilities";
const getNormalizedId = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value).trim();
};
const getProductIdentity = (product) => {
    var _a;
    const directId = getNormalizedId((_a = product === null || product === void 0 ? void 0 : product._id) !== null && _a !== void 0 ? _a : product === null || product === void 0 ? void 0 : product.productId);
    if (directId) {
        return directId;
    }
    const storeProductId = product === null || product === void 0 ? void 0 : product.storeProductId;
    if (typeof storeProductId === "string") {
        return getNormalizedId(storeProductId);
    }
    return getNormalizedId(storeProductId === null || storeProductId === void 0 ? void 0 : storeProductId._id);
};
const getDiscountTargetIdentity = (target) => {
    var _a, _b;
    if (typeof target === "string") {
        return getNormalizedId(target);
    }
    return getNormalizedId((_b = (_a = target === null || target === void 0 ? void 0 : target._id) !== null && _a !== void 0 ? _a : target === null || target === void 0 ? void 0 : target.productId) !== null && _b !== void 0 ? _b : target === null || target === void 0 ? void 0 : target.storeProductId);
};
const hasMatchingDiscountTarget = (targets = [], currentProduct) => {
    const currentId = getProductIdentity(currentProduct);
    if (!currentId) {
        return false;
    }
    return (Array.isArray(targets) ? targets : []).some((target) => {
        return getDiscountTargetIdentity(target) === currentId;
    });
};
export const calculateTotalTaxesOverPrice = (order, selectedCustomer, storeSettings, storeDiscounts, appliedOrderDiscounts, discountCodeObj) => {
    const productTableImports = [...order.products, ...order.buyAndGetProducts].map((current) => {
        var _a, _b, _c;
        let discPercentageInteger = 0;
        let productPercentageDiscount = 0;
        let customerDiscount = 0;
        const actualQuantity = Number((_b = (_a = current.qty) !== null && _a !== void 0 ? _a : current.quantity) !== null && _b !== void 0 ? _b : 0);
        if (isNoChargeReturnLine(order, current)) {
            return {
                product: current,
                qty: actualQuantity,
                productLineImportTotal: 0,
                productLineTotalWithDiscount: 0,
                productLineTaxesTotal: 0,
                taxBreakdown: [],
                lineDiscountAmount: 0,
                productLineSubtotal: 0,
                lineGrossBeforeDiscount: 0,
            };
        }
        const billableQuantity = resolveBillableQuantity(current);
        // Precio base sin impuestos (en modo over_price el precio es neto)
        const productBasePrice = order.express ? current.expressPrice : current.price;
        // === NUEVO: Considerar extraAmount en el precio a descontar ===
        // extraAmount es el ajuste total para la línea; se reparte por unidad
        const extraAmount = current.extraAmount || 0;
        const unitExtra = billableQuantity > 0 ? extraAmount / billableQuantity : 0;
        // Precio ajustado: suma del precio base y el extra por unidad
        const adjustedPrice = productBasePrice + unitExtra;
        // Cálculo de descuento sobre el precio ajustado
        if (!order.discountCode) {
            const discountsToApply = storeDiscounts.filter((discount) => discount.isActive && hasMatchingDiscountTarget(discount.products, current));
            customerDiscount = ((_c = selectedCustomer === null || selectedCustomer === void 0 ? void 0 : selectedCustomer.customer) === null || _c === void 0 ? void 0 : _c.discount) || 0;
            productPercentageDiscount = discountsToApply.reduce((acc, discount) => {
                return discount.type === 'percentage' ? acc + discount.value : acc;
            }, 0);
            discPercentageInteger = +((productPercentageDiscount + customerDiscount) / 100).toFixed(2);
            discountsToApply.forEach((discount) => (appliedOrderDiscounts[discount._id] = discount));
        }
        else {
            if ((discountCodeObj === null || discountCodeObj === void 0 ? void 0 : discountCodeObj.type) === DiscountCodeTypes.PERCENTAGE) {
                discPercentageInteger = +(discountCodeObj.value / 100).toFixed(2);
                if (!discountCodeObj.applyToAllProducts) {
                    discPercentageInteger = hasMatchingDiscountTarget(discountCodeObj.products, current)
                        ? +(discountCodeObj.value / 100).toFixed(2)
                        : 0;
                }
            }
            else if ((discountCodeObj === null || discountCodeObj === void 0 ? void 0 : discountCodeObj.type) === DiscountCodeTypes.NUMBER) {
                // Fixed-amount discount baked by `applyDiscountToProducts` as
                // `current.discountAmount` (line-total, NET pesos in over_price).
                // Per-unit treatment: derive `productDiscount = discountAmount / qty`
                // so the existing pipeline below subtracts pre-tax.
            }
            else if ((discountCodeObj === null || discountCodeObj === void 0 ? void 0 : discountCodeObj.type) === DiscountCodeTypes.BUY_X_GET_Y) {
                const condition = discountCodeObj.buyAndGetConditions[0];
                if (condition.getDiscountType === BuyAndGetConditionsTypes.PERCENTAGE && current.isBuyAndGetProduct) {
                    discPercentageInteger = +(condition.discountValue / 100).toFixed(2);
                }
                if (condition.getDiscountType === BuyAndGetConditionsTypes.FREE && current.isBuyAndGetProduct) {
                    discPercentageInteger = 1;
                }
            }
            else if ((discountCodeObj === null || discountCodeObj === void 0 ? void 0 : discountCodeObj.type) === DiscountCodeTypes.FREE_ITEM && current.isFreeItem) {
                discPercentageInteger = 1;
            }
        }
        // Calcular el descuento por unidad sobre el precio ajustado.
        // - PERCENTAGE/BUY_X_GET_Y/FREE_ITEM: derive from `discPercentageInteger`.
        // - NUMBER (fixed): `current.discountAmount` is the per-line NET discount
        //   in pesos, baked by `applyDiscountToProducts`. Convert to per-unit.
        let productDiscount = adjustedPrice * discPercentageInteger;
        const isFixedNumberCoupon = !!discountCodeObj &&
            discountCodeObj.type === DiscountCodeTypes.NUMBER &&
            Number(current.discountAmount) > 0;
        if (isFixedNumberCoupon && billableQuantity > 0) {
            const perUnitDiscount = Number(current.discountAmount) / billableQuantity;
            productDiscount = Math.min(adjustedPrice, perUnitDiscount);
        }
        const discountedAdjustedPrice = Math.max(0, adjustedPrice - productDiscount);
        // Subtotal para la línea (precio descontado por unidad * cantidad) SIN impuestos
        const subtotal = discountedAdjustedPrice * billableQuantity;
        // Calcular impuestos sobre el subtotal
        const taxPercentage = getProductTaxesPercentage(current, storeSettings) / 100;
        const taxes = subtotal * taxPercentage;
        // Resultados finales:
        const totalTaxesApplied = +taxes.toFixed(2);
        const taxBreakdown = calculateProductTaxBreakdown({
            product: current,
            store: storeSettings,
            lineTaxAmount: totalTaxesApplied,
        });
        const lineDiscount = +(productDiscount * billableQuantity).toFixed(2);
        // Precio total con descuento, reaplicando impuestos
        const prodLinePriceWithDiscount = +((subtotal * (1 + taxPercentage))).toFixed(2);
        // GROSS before discount: net-before-discount * (1 + tax). Needed to derive pricingBreakdown.
        const lineGrossBeforeDiscount = +((productBasePrice * billableQuantity + extraAmount) * (1 + taxPercentage)).toFixed(2);
        return {
            product: current,
            qty: actualQuantity,
            // Total neto original sin descuento: (precio base * cantidad) + extraAmount
            productLineImportTotal: (productBasePrice * billableQuantity) + extraAmount,
            // Total final con descuento, con impuestos incluidos
            productLineTotalWithDiscount: prodLinePriceWithDiscount,
            productLineTaxesTotal: totalTaxesApplied,
            taxBreakdown,
            lineDiscountAmount: lineDiscount,
            // Subtotal neto (sin impuestos) con descuento aplicado — for over_price this is NET after discount
            productLineSubtotal: subtotal,
            lineGrossBeforeDiscount, // GROSS before discount — needed to derive pricingBreakdown
        };
    });
    return productTableImports;
};
